<template>
  <div class="credits-slider-card" :class="{ 'no-select': isDisabled }" @copy="handleCopy">
    <div class="font-semibold text-[20px] text_color mb-1">Credits</div>
    <div class="text-sm text-[#888] mb-6">Credits are shared across users. Select how many credits you would like for your team below.</div>

    <div v-if="props.loading" class="flex justify-center items-center py-8">
      <div class="text-sm text-[#888]">Loading credit packs...</div>
    </div>
    <div v-else-if="creditPacks.length > 0" class="flex flex-col gap-4 relative">
      <div class="flex flex-row justify-between items-center mb-4">
        <div class="text-[20px] font-semibold text_color transition-all duration-300 ease-out">
          <span v-if="sliderValue !== -1"> {{ formatPlanCredits() }} + {{ displayValue }} credits/{{ billingPeriod === 'annual' ? 'yr' : 'mo' }} </span>
          <span v-else> {{ formatPlanCredits() }} ({{ planName }}) + {{ displayValue }} </span>
        </div>
        <div class="text-right">
          <div v-if="selectedPackPrice !== 'Talk to Sales'" class="text-[24px] font-bold text_color transition-all duration-300 ease-out">
            ${{ selectedPackPrice.toLocaleString() }}<span class="text-sm secondary_color">/{{ billingPeriod === 'annual' ? 'yr' : 'mo' }}</span>
          </div>
          <div v-else class="text-[24px] font-bold text_color transition-all duration-300 ease-out">
            {{ selectedPackPrice }}
          </div>
        </div>
      </div>
      <div class="slider-row">
        <div class="slider-track-wrapper">
          <div class="slider-bar-bg"></div>
          <div class="slider-bar-fill" :style="{ width: fillPercent + '%' }"></div>
          <input type="range" :min="0" :max="creditPacks.length - 1" :step="1" :value="sliderIndex" class="credits-slider" @input="handleSliderInput" :disabled="isDisabled" />
          <span class="slider-thumb" :style="thumbStyle">
            <ThemeImage :icon="imageNames.DRAG_ICON_GREY" alt="drag_icon" width="18" height="18" />
          </span>
        </div>
        <div class="slider-labels">
          <span
            v-for="(pack, index) in creditPacks"
            :key="pack.pack_size"
            :class="['slider-mark', sliderIndex === index ? 'active' : '']"
            :style="{ position: 'absolute', left: `${(index / (creditPacks.length - 1)) * 100}%`, transform: 'translateX(-50%)' }"
          >
            {{ formatSliderLabel(pack.pack_size) }}
          </span>
        </div>
      </div>

      <div v-if="isDisabled" class="disabled-overlay">
        <div class="lock-icon">
          <ThemeImage :icon="imageNames.LOCK_ICON" alt="lock" width="48" height="48" />
        </div>
        <div class="disabled-text">
          <div class="text-[14px] font-semibold mb-1">Select a paid plan to add credits</div>
          <div class="secondary_color text-[13px]">Credits will be available on paid plans only. Please select a paid plan to add additional credits</div>
        </div>
      </div>
    </div>
    <div v-else class="flex justify-center items-center py-8">
      <div class="text-sm text-[#888]">No credit packs available</div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import ThemeImage from '@/components/custom/ThemeImage.vue';
import { imageNames } from '@/javascript/imageConstants';
import { billingPeriodMap, getBillingPeriodSuffix } from '@/utils/billingPeriodUtils';

const props = defineProps({
  plan: {
    type: Object,
    default: () => ({}),
  },
  billingPeriod: {
    type: String,
    default: 'monthly',
  },
  creditPacksData: {
    type: Object,
    default: () => ({}),
  },
  loading: {
    type: Boolean,
    default: false,
  },
  currentPlanId: {
    type: String,
    default: 'free',
  },
  selectedPlanId: {
    type: String,
    default: 'free',
  },
  initialValue: {
    type: Number,
    default: 0,
  },
  planCredits: {
    type: Number,
    default: 0,
  },
  userSeats: {
    type: Number,
    default: 1,
  },
  skipInitialEmit: {
    type: Boolean,
    default: false,
  },
  resetSlider: {
    type: Boolean,
    default: false,
  },
  checkPaymentDue: {
    type: Function,
    default: null,
  },
});

const emit = defineEmits(['credit-pack-selected']);

const sliderValue = ref(0);
const sliderIndex = ref(0); // Track index instead of value
const initialValueSet = ref(false); // Track if initial value has been set

// Computed properties - Define these first before using them
const creditPacks = computed(() => {
  // Map frontend tab values to backend API keys
  const apiKey = billingPeriodMap[props.billingPeriod] || 'monthly';
  return props.creditPacksData[apiKey] || [];
});

const planName = computed(() => {
  if (props.selectedPlanId === 'starter') {
    return 'Starter plan';
  } else if (props.selectedPlanId === 'growth') {
    return 'Growth plan';
  } else if (props.selectedPlanId === 'free') {
    return 'Free plan';
  }
  return 'Plan';
});

const displayValue = computed(() => {
  if (sliderValue.value === -1) return 'Custom Credits';
  return formatPackSize(sliderValue.value);
});

const selectedPackPrice = computed(() => {
  const pack = creditPacks.value[sliderIndex.value];
  if (pack && pack.isCustom) return 'Talk to Sales';
  return pack ? pack.pack_price : 0;
});

const fillPercent = computed(() => {
  if (creditPacks.value.length <= 1) return 0;
  return (sliderIndex.value / (creditPacks.value.length - 1)) * 100;
});

const thumbStyle = computed(() => {
  return {
    left: `calc(${fillPercent.value}% - 6px)`,
  };
});

const isDisabled = computed(() => {
  return props.selectedPlanId === 'free';
});

const handleCopy = (e) => {
  if (isDisabled.value) {
    e.preventDefault();
  }
};

// Helper functions
function formatPackSize(size) {
  if (size === -1) {
    return 'Custom';
  }
  if (size === 0) {
    return '0';
  }
  if (size >= 1000000) {
    return `${size / 1000000}m`;
  } else if (size >= 1000) {
    return `${size / 1000}K`;
  }
  return size.toString();
}

function formatSliderLabel(size) {
  if (size === -1) {
    return 'Custom';
  }
  if (size === 0) {
    return '0';
  }
  if (size >= 1000000) {
    return `${(size / 1000000).toFixed(1)}M`;
  } else if (size >= 1000) {
    const suffix = getBillingPeriodSuffix(props.billingPeriod);
    const value = size / 1000;
    // Show decimal only if not a whole number
    return `${value % 1 === 0 ? value.toFixed(0) : value.toFixed(1)}K${suffix}`;
  }
  const suffix = getBillingPeriodSuffix(props.billingPeriod);
  return `${size}${suffix}`;
}

function formatPlanCredits() {
  if (props.planCredits >= 1000000) {
    return `${(props.planCredits / 1000000).toFixed(1)}M`;
  } else if (props.planCredits >= 1000) {
    const value = props.planCredits / 1000;
    // Show decimal only if not a whole number
    return `${value % 1 === 0 ? value.toFixed(0) : value.toFixed(1)}K`;
  }
  return props.planCredits.toString();
}

// Update slider value based on current billing period
const updateSliderValue = async () => {
  // Check if we have an initial value to set and haven't set it yet
  if (!initialValueSet.value && props.initialValue > 0 && creditPacks.value.length > 0) {
    const matchingIndex = creditPacks.value.findIndex((pack) => pack.pack_size === props.initialValue);
    if (matchingIndex !== -1) {
      sliderIndex.value = matchingIndex;
      sliderValue.value = props.initialValue;
      initialValueSet.value = true;

      // Only emit if not skipping initial emit
      if (!props.skipInitialEmit) {
        const pack = creditPacks.value[matchingIndex];
        if (pack && pack.pack_size > 0) {
          emit('credit-pack-selected', {
            pack_size: pack.pack_size,
            pack_price: pack.pack_price,
            billing_period: props.billingPeriod,
            isCustom: pack.isCustom || false,
          });
        }
      }
      return;
    }
  }

  // Default to first position only if not already set
  if (!initialValueSet.value) {
    sliderIndex.value = 0;
    sliderValue.value = creditPacks.value.length > 0 ? creditPacks.value[0].pack_size : 0;
  }
};

const handleSliderInput = async (event) => {
  if (isDisabled.value) return;

  const newIndex = parseInt(event.target.value);
  const oldIndex = sliderIndex.value;

  // Don't do anything if the value hasn't changed
  if (newIndex === oldIndex) return;

  // Temporarily update to show the change
  sliderIndex.value = newIndex;

  // Check if payment is due using the prop function
  let paymentOK = true; // Default to true if no check function provided

  if (props.checkPaymentDue && typeof props.checkPaymentDue === 'function') {
    paymentOK = await props.checkPaymentDue();
  }

  if (paymentOK === false) {
    // Revert the slider to its previous position
    sliderIndex.value = oldIndex;
    return;
  }

  // Payment is OK, the new value stays (already set above)
};

// Watch for billing period changes
watch(
  () => props.billingPeriod,
  () => {
    updateSliderValue();
  },
  { immediate: true }
);

// Watch for credit packs data changes
watch(
  () => props.creditPacksData,
  () => {
    updateSliderValue();
  },
  { deep: true }
);

// Watch for disabled state changes and reset slider
watch(isDisabled, (disabled) => {
  if (disabled) {
    sliderIndex.value = 0;
    sliderValue.value = 0;
    emit('credit-pack-selected', null);
  } else {
    // When re-enabled, restore the initial value if available
    if (props.initialValue > 0 && creditPacks.value.length > 0) {
      const matchingIndex = creditPacks.value.findIndex((pack) => pack.pack_size === props.initialValue);
      if (matchingIndex !== -1) {
        sliderIndex.value = matchingIndex;
        sliderValue.value = props.initialValue;
      }
    }
  }
});

// Watch for slider index changes and emit selected pack
watch(sliderIndex, (newIndex) => {
  if (isDisabled.value) {
    emit('credit-pack-selected', null);
    return;
  }

  const pack = creditPacks.value[newIndex];
  if (pack) {
    sliderValue.value = pack.pack_size;
    if (pack.pack_size === 0) {
      emit('credit-pack-selected', null);
    } else {
      emit('credit-pack-selected', {
        pack_size: pack.pack_size,
        pack_price: pack.pack_price,
        billing_period: props.billingPeriod,
        isCustom: pack.isCustom || false,
      });
    }
  }
});

// Watch for reset trigger
watch(
  () => props.resetSlider,
  (newValue) => {
    if (newValue) {
      // Reset to first position (0 credits)
      sliderIndex.value = 0;
      sliderValue.value = creditPacks.value.length > 0 ? creditPacks.value[0].pack_size : 0;
      emit('credit-pack-selected', null);
    }
  }
);

// Watch for initialValue changes (when switching tabs)
watch(
  () => props.initialValue,
  (newValue) => {
    if (newValue >= 0 && creditPacks.value.length > 0) {
      const matchingIndex = creditPacks.value.findIndex((pack) => pack.pack_size === newValue);
      if (matchingIndex !== -1) {
        sliderIndex.value = matchingIndex;
        sliderValue.value = newValue;
      } else {
        // If no match, reset to 0
        sliderIndex.value = 0;
        sliderValue.value = creditPacks.value[0].pack_size || 0;
      }
    }
  }
);

// Initialize slider value when component mounts
onMounted(() => {
  updateSliderValue();
});
</script>

<style scoped>
.no-select,
.no-select * {
  -webkit-touch-callout: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
}

.credits-slider-card {
  background: var(--white-opacity-4);
  border: 1px solid var(--white-opacity-8);
  border-radius: 16px;
  padding: 32px 32px 24px 32px;
  width: 100%;
  margin: 0 auto;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.03);
  position: relative;
  isolation: isolate;
}

.disabled-overlay {
  position: absolute;
  top: -24px;
  left: -32px;
  right: -32px;
  bottom: -24px;
  background: transparent;
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 10;
  border-radius: 16px;
  overflow: hidden;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
  pointer-events: none;
}

.no-select .disabled-overlay,
.no-select .disabled-overlay * {
  pointer-events: auto;
}

.lock-icon {
  margin-bottom: 8px;
  opacity: 0.7;
}

.disabled-text {
  text-align: center;
  max-width: 380px;
}

.disabled-title {
  font-weight: 600;
  font-size: 14px;
  margin-bottom: 8px;
}

.slider-row {
  width: 100%;
}
.slider-track-wrapper {
  position: relative;
  width: 100%;
  height: 22px;
  display: flex;
  align-items: center;
}
.slider-bar-bg {
  position: absolute;
  top: 50%;
  left: 0;
  width: 100%;
  height: 2px;
  background: #e0e0e0;
  border-radius: 1px;
  transform: translateY(-50%);
  z-index: 1;
}
.slider-bar-fill {
  position: absolute;
  top: 0;
  left: 0;
  height: 22px;
  background: linear-gradient(90deg, #7095eb 0%, #b343cb 100%);
  border-radius: 32px 6px 6px 32px;
  z-index: 2;
  transition: width 0.15s ease-out;
}
.credits-slider {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 22px;
  opacity: 0;
  z-index: 4;
  margin: 0;
  cursor: pointer;
}
.credits-slider:disabled {
  cursor: not-allowed;
}
.credits-slider:not(:disabled):hover ~ .slider-thumb {
  transform: translateY(-50%) scale(1.1);
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.2);
}
.slider-thumb {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 16px;
  height: 22px;
  border-radius: 14px;
  background: #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 5;
  pointer-events: none;
  transition:
    left 0.15s ease-out,
    transform 0.15s ease-out,
    box-shadow 0.15s ease-out;
}
.slider-labels {
  width: 100%;
  font-size: 15px;
  color: #888;
  user-select: none;
  position: relative;
  height: 20px;
  margin-top: 8px;
}
.slider-mark {
  min-width: 32px;
  text-align: center;
  font-weight: 500;
  transition: color 0.2s;
  white-space: nowrap;
}
.slider-mark.active {
  color: #222;
  font-weight: bold;
}
</style>
